(() => {
	const BTN_SELECTOR = "[data-btn-whatsapp]"; // your button(s)
	const PHONE = "5511975631837"; // WhatsApp phone without + or special chars

	// ---------- Helpers ----------
	const elText = (sel, ctx = document) => ctx.querySelector(sel);
	const els = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

	// Get the nearest <product-info> wrapper (works on product page & quick views)
	const getProductRoot = (node) =>
		node?.closest?.("product-info") ||
		document.querySelector("product-info") ||
		document;

	// Parse the full product JSON (you render this in product.liquid as #ProductData)
	const getProductJSON = (root) => {
		try {
			const jsonEl = elText("#ProductData", root);
			if (jsonEl?.textContent) return JSON.parse(jsonEl.textContent);
		} catch (_) {}
		return null;
	};

	// Get current variant id from the hidden input
	const getVariantIdFromInput = (root) => {
		const idInput = elText('input[name="id"]', root);
		if (!idInput) return null;
		const vId = Number(idInput.value);
		return Number.isFinite(vId) ? vId : null;
	};

	// Find current variant using id + product JSON
	const getCurrentVariant = (root) => {
		const product = getProductJSON(root);
		const id = getVariantIdFromInput(root);
		if (!product || !id) return { product, variant: null };
		const variant = product.variants?.find((v) => v.id === id) || null;
		return { product, variant };
	};

	// Find option indices by name so "Tamanho" and "Cor" never flip.
	// Accepts common Portuguese/English labels and ignores case/accents.
	const normalize = (s) =>
		String(s || "")
			.normalize("NFD")
			.replace(/\p{Diacritic}/gu, "")
			.toLowerCase()
			.trim();

	const isSizeName = (name) => {
		const n = normalize(name);
		return ["tamanho", "tam", "size"].includes(n);
	};

	const isColorName = (name) => {
		const n = normalize(name);
		return ["cor", "color", "cor:", "cor ", "color:", "cores", "cores:"].some(
			(k) => n.startsWith(k) || n === k,
		);
	};

	// Build a consistent { size, color, rest: [{label, value}, ...] }
	const mapVariantOptions = (product, variant) => {
		const result = { size: null, color: null, rest: [] };
		if (!product?.options || !variant?.options) return result;

		product.options.forEach((optName, i) => {
			const value = variant.options[i];
			if (isSizeName(optName)) {
				result.size = value;
			} else if (isColorName(optName)) {
				result.color = value;
			} else {
				result.rest.push({ label: optName, value });
			}
		});

		return result;
	};

	// Build a clean message (keeps order: Tamanho then Cor)
	const buildMessage = ({ title, priceText, url, mapped }) => {
		const parts = [];

		if (mapped.size) {
			parts.push(`Uso o tamanho: ${mapped.size}`);
		}
		if (mapped.color) {
			parts.push(`e gostei da cor ${mapped.color}`);
		}

		// Include any extra options if you have more than size/color
		if (mapped.rest?.length) {
			const extras = mapped.rest
				.map(({ label, value }) => `${label}: ${value}`)
				.join("; ");
			parts.push(`(${extras})`);
		}

		const body =
			`Olá! Me interessei pelo produto *${title}*, que vi por ${priceText} no site.` +
			`\n\nGostaria de realizar a compra por aqui! ${parts.join(" ")}.` +
			`\n\nLink do produto:\n${url}`;

		return body;
	};

	// Format BRL (optional; if you already pass price via data-price, keep it)
	const formatBRL = (cents) => {
		try {
			return new Intl.NumberFormat("pt-BR", {
				style: "currency",
				currency: "BRL",
			}).format(cents / 100);
		} catch (_) {
			// fallback
			return `R$ ${(cents / 100).toFixed(2).replace(".", ",")}`;
		}
	};

	// Update a single button with the current variant info
	const updateButtonFromVariant = (
		btn,
		root,
		{ forceUrlUpdate = true } = {},
	) => {
		const { product, variant } = getCurrentVariant(root);
		if (!btn || !product || !variant) return;

		// Title / URL from data-* or fallback from product
		const title = btn.dataset.title || product.title || "";
		const baseUrl = (btn.dataset.url || product.url || location.pathname).split(
			"?",
		)[0];
		const url = `${baseUrl}?variant=${variant.id}`;

		// Price: prefer dataset.price; otherwise format from variant.price
		const priceText = btn.dataset.price || formatBRL(variant.price);

		const mapped = mapVariantOptions(product, variant);

		// Persist a stable, readable variantTitle in a fixed order (Size$Color)
		btn.dataset.variantTitle =
			`${mapped.size || ""}$${mapped.color || ""}`.replace(/\$$/, "");

		if (forceUrlUpdate) {
			btn.dataset.url = url;
		}

		// Pre-build and cache message so the click is instant
		const message = buildMessage({
			title,
			priceText,
			url: `${location.origin}${url}`,
			mapped,
		});
		btn.dataset.whatsappMessage = message;
	};

	// Handle click -> open WhatsApp with the prebuilt message
	const handleWhatsAppClick = (btn) => {
		const message = btn.dataset.whatsappMessage;
		if (!message) return; // nothing to send
		const whatsappUrl = `https://wa.me/${PHONE}?text=${encodeURIComponent(message)}`;
		window.open(whatsappUrl, "_blank");
	};

	// ---------- Bootstrapping ----------
	const initForRoot = (root) => {
		// Prime all buttons immediately (first page open)
		els(BTN_SELECTOR, root).forEach((btn) => {
			updateButtonFromVariant(btn, getProductRoot(btn));
		});

		// Listen to Pub/Sub (FoxTheme)
		if (
			window.FoxThemeEvents?.subscribe &&
			window.PUB_SUB_EVENTS?.variantChange
		) {
			window.FoxThemeEvents.subscribe(
				PUB_SUB_EVENTS.variantChange,
				({ data }) => {
					els(BTN_SELECTOR, root).forEach((btn) => {
						updateButtonFromVariant(btn, getProductRoot(btn));
					});
				},
			);
		}

		// Custom DOM events your theme already dispatches
		const onChanged = () => {
			els(BTN_SELECTOR, root).forEach((btn) => {
				updateButtonFromVariant(btn, getProductRoot(btn));
			});
		};

		document.addEventListener("variant:change", onChanged);
		document.addEventListener("variant:changed", onChanged);
		document.addEventListener("product:variant-change", onChanged);

		// Fallback: observe hidden input[name="id"] for programmatic changes
		const idInput = elText('input[name="id"]', root);
		if (idInput) {
			let last = idInput.value;
			const updateIfChanged = () => {
				if (idInput.value === last) return;
				last = idInput.value;
				onChanged();
			};
			idInput.addEventListener("change", updateIfChanged);
			idInput.addEventListener("input", updateIfChanged);
			const mo = new MutationObserver(updateIfChanged);
			mo.observe(idInput, { attributes: true, attributeFilter: ["value"] });
		}

		// Also after product media initializes (in case of late mounts)
		document.addEventListener("product-media:loaded", onChanged, {
			once: true,
		});
	};

	// Global click
	document.addEventListener("click", (e) => {
		const btn = e.target.closest(BTN_SELECTOR);
		if (!btn) return;
		e.preventDefault();
		handleWhatsAppClick(btn);
	});

	// DOM ready
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => initForRoot(document));
	} else {
		initForRoot(document);
	}
})();
